import { View, StyleSheet } from 'react-native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { NotificationTemplateForAdmin } from '@sapphicsavvy/business';
import { NotificationCard } from './NotificationCard';
import { TemplateHash } from './types';
import {
    createNotificationTemplateHash,
    extractNormalizedTemplateFields,
    objectCompare,
    sortNotificationTemplates,
} from './utils';

import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../../constants/textStyles';
import { getColors } from '../../../../../constants/colors';
import { quarterSharedStyles } from '../quarter/quarterSharedStyles';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { ScrollView } from 'react-native';
import { CustomNotificationCard } from './CustomNotificationCard';
import { TestModal } from './TestModal';
import { utils } from '../../../../../utils/utils';

const COLORS = getColors();

export function NotificationGrid() {
    const { data: templates, refetch: refetchTemplates } =
        trpc.admin.notification.getTemplates.useQuery();
    const { mutate: updateTemplates, isLoading: updateLoading } =
        trpc.admin.notification.updateTemplates.useMutation();
    const [templateState, setTemplateState] = useState<TemplateHash | null>(
        null,
    );
    const [selectedTemplate, setSelectedTemplate] =
        useState<NotificationTemplateForAdmin | null>(null);

    const disabledButtons = useMemo(() => {
        if (!templates || !templateState) return true;

        const originalTemplateHash = createNotificationTemplateHash(
            templates as NotificationTemplateForAdmin[],
        );

        return objectCompare(originalTemplateHash, templateState);
    }, [templates, templateState]);

    useEffect(() => {
        if (templates) {
            setTemplateState(
                createNotificationTemplateHash(
                    templates as NotificationTemplateForAdmin[],
                ),
            );
        }
    }, [templates]);

    const saveAll = useCallback(() => {
        if (!templateState) return;
        updateTemplates(
            {
                data: extractNormalizedTemplateFields(templateState),
            },
            {
                onSuccess: () => {
                    refetchTemplates(),
                        utils.showToast(
                            'success',
                            'Successfully saved changes!',
                        );
                },
            },
        );
    }, [templateState]);

    const cancel = useCallback(() => {
        setTemplateState(() =>
            createNotificationTemplateHash(
                templates as NotificationTemplateForAdmin[],
            ),
        );
    }, [templates, createNotificationTemplateHash]);

    let [quarterTemplates, customTemplates] = useMemo(() => {
        if (!templateState) {
            return [[], []];
        }
        return sortNotificationTemplates(Object.values(templateState));
    }, [templateState]);

    const createGridRows = useCallback((templates) => {
        const rows = [];
        for (let i = 0; i < templates.length; i += 2) {
            rows.push(templates.slice(i, i + 2));
        }
        return rows;
    }, []);

    const gridRows = useMemo(() => {
        return createGridRows(quarterTemplates || []);
    }, [quarterTemplates, createGridRows]);

    return (
        <ScrollView
            style={{ flex: 1, paddingBottom: 40 }}
            contentContainerStyle={{ flexGrow: 1 }}
        >
            <TestModal
                template={selectedTemplate}
                setTemplate={setSelectedTemplate}
            />
            <View style={styles.buttonRow}>
                <View style={styles.buttonContainer}>
                    <MB_Button
                        title={'Cancel'}
                        onPress={cancel}
                        style={[
                            quarterSharedStyles.secondaryBtn,
                            { width: 150 },
                        ]}
                        textStyle={quarterSharedStyles.secondaryBtnText}
                        disabled={disabledButtons || updateLoading}
                    />
                    <MB_Button
                        title={'Save Changes'}
                        onPress={saveAll}
                        style={[quarterSharedStyles.button, { marginLeft: 20 }]}
                        textStyle={quarterSharedStyles.buttonText}
                        disabled={disabledButtons || updateLoading}
                    />
                </View>
            </View>

            {/* Grid layout with rows of 2 cards each */}
            {gridRows.map((row, rowIndex) => (
                <View key={`row-${rowIndex}`} style={styles.gridRow}>
                    {row.map((template: NotificationTemplateForAdmin) => (
                        <View key={template.type} style={styles.gridItem}>
                            <NotificationCard
                                template={template}
                                setTemplate={setTemplateState}
                                onTestPress={() =>
                                    setSelectedTemplate(template)
                                }
                            />
                        </View>
                    ))}
                    {/* If the row has only one card, add an empty space to maintain grid layout */}
                    {row.length === 1 && <View style={styles.gridItem} />}
                </View>
            ))}

            {customTemplates.map((template) => {
                return (
                    <View key={template.type} style={styles.customGridItem}>
                        <CustomNotificationCard
                            template={template}
                            setTemplate={setTemplateState}
                            onTestPress={() => setSelectedTemplate(template)}
                        />
                    </View>
                );
            })}
        </ScrollView>
    );
}

const styles = StyleSheet.create({
    buttonRow: {
        width: '100%',
        alignItems: 'flex-end',
        marginBottom: 25,
    },
    buttonContainer: {
        flexDirection: 'row',
    },
    gridRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    gridItem: {
        width: '48%',
    },
    customGridItem: {
        width: '96%',
    },
    columnLabel: mbTextStyles([
        textStyles.largerText,
        {
            fontSize: 14,
            color: COLORS.greyText,
            paddingBottom: 8,
        },
    ]),
});
